import { Switch, Tooltip } from 'antd'
import { DataLayer } from 'features/systemLens'
import React from 'react'
import styled from 'styled-components'

const DataLayersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
`

const LayerIcon = styled.span<{ $enabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: ${({ $enabled, theme }) =>
    $enabled ? '#00c290' : theme.forecast?.defaultText};
`

const LayerName = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast?.activeText};
  white-space: nowrap;
  flex: 1;
  line-height: 1.2;
`

const DataLayerItem = styled.div<{ $enabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  background: ${({ theme, $enabled }) =>
    $enabled ? `${theme.forecast?.background3}` : theme.forecast?.background2};
  border: 1px solid
    ${({ $enabled }) => ($enabled ? 'rgba(0, 194, 144, 0.15)' : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.forecast?.background3};
    border-color: ${({ $enabled }) =>
      $enabled ? 'rgba(0, 194, 144, 0.25)' : 'rgba(0, 194, 144, 0.1)'};
  }
`

interface DataLayersControlProps {
  dataLayers: DataLayer[]
  onToggleLayer: (layerId: string) => void
  operationalEventsCount?: number
}

export const DataLayersControl: React.FC<DataLayersControlProps> = ({
  dataLayers,
  onToggleLayer,
  operationalEventsCount = 0,
}) => {
  return (
    <DataLayersGrid>
      {dataLayers.map((layer) => {
        // Check if this is the events layer and if there are no events
        const isEventsLayer = layer.id === 'events'
        const hasNoEvents = isEventsLayer && operationalEventsCount === 0

        const switchElement = (
          <Switch
            checked={layer.enabled}
            onChange={() => onToggleLayer(layer.id)}
            size='small'
            disabled={hasNoEvents}
          />
        )

        return (
          <DataLayerItem key={layer.id} $enabled={layer.enabled}>
            <LeftContainer>
              <LayerIcon $enabled={layer.enabled}>{layer.icon}</LayerIcon>
              <LayerName>
                {layer.name}
                {isEventsLayer && operationalEventsCount > 0 && (
                  <span
                    style={{
                      marginLeft: '0.1875rem',
                      fontSize: '0.6875rem',
                      opacity: 0.5,
                      fontWeight: 'normal',
                    }}>
                    ({operationalEventsCount})
                  </span>
                )}
              </LayerName>
            </LeftContainer>
            {hasNoEvents ? (
              <Tooltip
                title='No work orders available for this asset. Create work orders to see maintenance and inspection events on the timeline.'
                placement='left'>
                {switchElement}
              </Tooltip>
            ) : (
              switchElement
            )}
          </DataLayerItem>
        )
      })}
    </DataLayersGrid>
  )
}
